import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface TranslationResult {
  translatedText: string;
  isLoading: boolean;
  error: string | null;
}

// Google Translate API를 사용한 번역 함수
const translateText = async (text: string, targetLang: 'ko' | 'en'): Promise<string> => {
  if (targetLang === 'en') {
    return text; // 영어는 원본 그대로 반환
  }

  try {
    // Google Translate API 사용
    const response = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`);
    const data = await response.json();
    
    if (data && data[0] && data[0][0] && data[0][0][0]) {
      return data[0][0][0];
    }
    
    // API 실패 시 폴백 번역 사용
    return getFallbackTranslation(text, targetLang);
  } catch (error) {
    console.warn('Google Translate API failed, using fallback:', error);
    return getFallbackTranslation(text, targetLang);
  }
};

// 폴백 번역 사전
const getFallbackTranslation = (text: string, targetLang: 'ko' | 'en'): string => {
  // 네비게이션 메뉴는 항상 영어로 유지
  if (text === 'Works' || text === 'Activities' || text === 'About') {
    return text;
  }
  
  if (targetLang === 'ko') {
    const translations: { [key: string]: string } = {
      'New QR Order Solution': '새로운 QR 주문 솔루션',
      'QR Designing a Web Service for Restaurant Food Ordering and Payment via QR Code': 'QR 코드를 통한 음식 주문 및 결제를 위한 웹 서비스 디자인',
      'Karrot(Daangn)': '당근',
      'Project managing, Product & UXUI Design': '프로젝트 관리, 제품 및 UX/UI 디자인',
      'Background': '배경',
      'QR Order is a web service that allows users to order food and pay for it via QR code.': 'QR 주문은 사용자가 QR 코드를 통해 음식을 주문하고 결제할 수 있는 웹 서비스',
      'Obtaining Consent for Personalized Advertising': '개인 맞춤형 광고 동의 얻기',
      'Obtain personalized advertising consent from new users to serve tailored ads.': '맞춤형 광고를 제공하기 위해 신규 사용자로부터 개인 맞춤형 광고 동의 얻기',
      'UX Strategic Planning, Research, UXUI Design': 'UX 전략 기획, 연구, UX/UI 디자인',
      'New Video Ads': '새로운 비디오 광고',
      'Introducing new vertical video ads in the home feed.': '홈 피드에 새로운 세로형 비디오 광고 도입',
      'UXUI Design, Managing the Experiment': 'UX/UI 디자인, 실험 관리',
      'A Bidding System in Search Ads': '검색 광고의 입찰 시스템',
      'Designing a search advertising bidding system that anyone can easily use on mobile': '모든 사람이 모바일에서 쉽게 사용할 수 있는 검색 광고 입찰 시스템 설계',
      'UXUI Design': 'UX/UI 디자인',
      'New Catalog Manager for Ads': '광고용 새로운 카탈로그 매니저',
      'Design a catalog management platform that can manage and advertise millions of products for Karrot.': '당근을 위해 수백만 개의 제품을 관리하고 광고할 수 있는 카탈로그 관리 플랫폼을 설계합니다.',
      'User Research, Service Planning, UXUI Design': '사용자 연구, 서비스 기획, UX/UI 디자인',
      'New Outlink-Type Ads': '새로운 아웃링크형 광고',
      'Introducing a new outlink-type advertisement for small businesses.': '소상공인을 위한 새로운 아웃링크형 광고 도입',
      'Eliminating Concerns of Creating Ads': '광고 생성에 대한 우려 해소',
      'Encouraging ad creation to increase the activation rate.': '활성화율을 높이기 위해 광고 생성 유도하기',
      'New Service Design': '새로운 서비스 디자인',
      'First User Process Design': '첫 사용자 프로세스 디자인',
      'Onboarding process design on bookcovery to make activation.': '활성화를 위한 북커버리 온보딩 프로세스 설계',
      'Service Planning, UXUI Design': '서비스 기획, UX/UI 디자인',
      'Store UX/UI Design': '스토어 UX/UI 디자인',
      'Mobile store tap UX/UI design on Styleshare to maximize store tap using rates and purchase amout.': 'Styleshare에서 스토어 탭 사용률과 구매 금액을 최대화하기 위한 모바일 스토어 탭 UX/UI 디자인',
      'Leading More Business Users to Advertising': '더 많은 비즈니스 사용자를 광고로 유도',
      'An experiment of UX to induce users to start advertising.': '사용자가 광고를 시작하도록 유도하는 UX 실험',
      'Improvement the mission contents UX': '미션 콘텐츠 UX 개선',
      'Improving content UX to increase mission completion rate.': '미션 완료율을 높이기 위한 콘텐츠 UX 개선',
      'Minding': '마인딩',
      'Improving UI/UX of Top Search Ad Placement': '상위 검색 광고 배치의 UI/UX 개선',
      'Improving the design of top-position search ads to increase user engagement and click-through rate': '사용자 참여도와 클릭률을 높이기 위한 상위 위치 검색 광고 디자인 개선',
      'Managing the Experiment': '실험 관리',
    };
    
    return translations[text] || text;
  } else {
    // 네비게이션 메뉴는 항상 영어로 유지
    if (text === '작품' || text === '활동' || text === '소개') {
      return text;
    }
    
    const translations: { [key: string]: string } = {
      '새로운 QR 주문 솔루션': 'New QR Order Solution',
      'QR 코드를 통한 레스토랑 음식 주문 및 결제를 위한 웹 서비스 설계': 'QR Designing a Web Service for Restaurant Food Ordering and Payment via QR Code',
      '당근(당근마켓)': 'Karrot(Daangn)',
      '프로젝트 관리, 제품 및 UX/UI 디자인': 'Project managing, Product & UXUI Design',
      '배경': 'Background',
      'QR 주문은 사용자가 QR 코드를 통해 음식을 주문하고 결제할 수 있는 웹 서비스': 'QR Order is a web service that allows users to order food and pay for it via QR code.',
      '개인 맞춤형 광고 동의 얻기': 'Obtaining Consent for Personalized Advertising',
      '맞춤형 광고를 제공하기 위해 신규 사용자로부터 개인 맞춤형 광고 동의 얻기': 'Obtain personalized advertising consent from new users to serve tailored ads.',
      'UX 전략 기획, 연구, UX/UI 디자인': 'UX Strategic Planning, Research, UXUI Design',
      '새로운 비디오 광고': 'New Video Ads',
      '홈 피드에 새로운 세로형 비디오 광고 도입': 'Introducing new vertical video ads in the home feed.',
      'UX/UI 디자인, 실험 관리': 'UXUI Design, Managing the Experiment',
      '검색 광고의 입찰 시스템': 'A Bidding System in Search Ads',
      '모든 사람이 모바일에서 쉽게 사용할 수 있는 검색 광고 입찰 시스템 설계': 'Designing a search advertising bidding system that anyone can easily use on mobile',
      'UX/UI 디자인': 'UXUI Design',
      '광고용 새로운 카탈로그 매니저': 'New Catalog Manager for Ads',
      '당근을 위해 수백만 개의 제품을 관리하고 광고할 수 있는 카탈로그 관리 플랫폼을 설계합니다.': 'Design a catalog management platform that can manage and advertise millions of products for Karrot.',
      '사용자 연구, 서비스 기획, UX/UI 디자인': 'User Research, Service Planning, UXUI Design',
      '새로운 아웃링크형 광고': 'New Outlink-Type Ads',
      '소상공인을 위한 새로운 아웃링크형 광고 도입': 'Introducing a new outlink-type advertisement for small businesses.',
      '광고 생성에 대한 우려 해소': 'Eliminating Concerns of Creating Ads',
      '활성화율을 높이기 위해 광고 생성을 유도하기': 'Encouraging ad creation to increase the activation rate.',
      '새로운 서비스 디자인': 'New Service Design',
      '첫 사용자 프로세스 디자인': 'First User Process Design',
      '활성화를 위한 북커버리 온보딩 프로세스 설계': 'Onboarding process design on bookcovery to make activation.',
      '서비스 기획, UX/UI 디자인': 'Service Planning, UXUI Design',
      '스토어 UX/UI 디자인': 'Store UX/UI Design',
      'Styleshare에서 스토어 탭 사용률과 구매 금액을 최대화하기 위한 모바일 스토어 탭 UX/UI 디자인': 'Mobile store tap UX/UI design on Styleshare to maximize store tap using rates and purchase amout.',
      '더 많은 비즈니스 사용자를 광고로 유도': 'Leading More Business Users to Advertising',
      '사용자가 광고를 시작하도록 유도하는 UX 실험': 'An experiment of UX to induce users to start advertising.',
      '미션 콘텐츠 UX 개선': 'Improvement the mission contents UX',
      '미션 완료율을 높이기 위한 콘텐츠 UX 개선': 'Improving content UX to increase mission completion rate.',
      '마인딩': 'Minding',
      '상위 검색 광고 배치의 UI/UX 개선': 'Improving UI/UX of Top Search Ad Placement',
      '사용자 참여도와 클릭률을 높이기 위한 상위 위치 검색 광고 디자인 개선': 'Improving the design of top-position search ads to increase user engagement and click-through rate',
      '실험 관리': 'Managing the Experiment',
    };
    
    return translations[text] || text;
  }
};

export const useTranslation = (text: string): TranslationResult => {
  const { language } = useLanguage();
  const [translatedText, setTranslatedText] = useState(text);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // 네비게이션 메뉴는 항상 영어로 유지
    if (text === 'Works' || text === 'Activities' || text === 'About') {
      setTranslatedText(text);
      return;
    }

    if (language === 'en') {
      setTranslatedText(text);
      return;
    }

    // 빈 텍스트나 공백만 있는 경우 처리
    if (!text || text.trim() === '') {
      setTranslatedText(text);
      return;
    }

    setIsLoading(true);
    setError(null);

    translateText(text, language)
      .then((result) => {
        setTranslatedText(result);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Translation error:', err);
        setError(err.message);
        setIsLoading(false);
        setTranslatedText(text); // 오류 시 원본 텍스트 사용
      });
  }, [text, language]);

  return { translatedText, isLoading, error };
};
